import React, { useState, useEffect, useCallback } from 'react';
import { Joyride, EVENTS } from 'react-joyride';
import type { Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { APP_HOME } from '../../utils/segment';

interface GuidedTourProps {
  run: boolean;
  onComplete: () => void;
}

const TOUR_COMPLETED_KEY = 'snapfy_tour_completed';

const GuidedTour: React.FC<GuidedTourProps> = ({ run, onComplete }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [steps, setSteps] = useState<Step[]>([]);
  const [shouldRun, setShouldRun] = useState(false);

  const buildSteps = useCallback((pathname: string): Step[] => {
    if (pathname === APP_HOME) {
      return [
        {
          target: '#tour-home',
          content: t('tour.home.welcome', 'Bem-vindo ao Snapfy! Aqui é a sua página inicial, onde você tem uma visão geral de tudo.'),
          title: t('tour.home.welcomeTitle', 'Início'),
          skipBeacon: true,
        },
        {
          target: '#tour-minha-area',
          content: t('tour.home.myArea', 'Aqui ficam sua Galeria, Brandbook e Modelos salvos.'),
          title: t('tour.home.myAreaTitle', 'Minha Área'),
        },
        {
          target: '#tour-apps',
          content: t('tour.home.apps', 'Todos os apps de geração de imagem e vídeo ficam aqui.'),
          title: t('tour.home.appsTitle', 'Apps'),
        },
      ];
    }

    if (pathname === '/batch') {
      return [
        {
          target: '#tour-fashion-style',
          content: t('tour.batch.style', 'Escolha o estilo fotográfico para todas as suas fotos.'),
          title: t('tour.batch.styleTitle', 'Estilo'),
          skipBeacon: true,
        },
        {
          target: '#tour-fashion-clothes',
          content: t('tour.batch.clothes', 'Envie as peças de roupa que deseja vestir no modelo.'),
          title: t('tour.batch.clothesTitle', 'Roupas'),
        },
        {
          target: '#tour-fashion-model',
          content: t('tour.batch.model', 'Selecione um modelo virtual ou descreva o seu.'),
          title: t('tour.batch.modelTitle', 'Modelo'),
        },
      ];
    }

    if (pathname === '/brandbook') {
      return [
        {
          target: '#tour-brandbook-header',
          content: t('tour.brandbook.header', 'Analise a identidade visual de qualquer marca a partir da URL.'),
          title: t('tour.brandbook.headerTitle', 'Brandbook'),
          skipBeacon: true,
        },
      ];
    }

    return [];
  }, [t]);

  useEffect(() => {
    if (!run) {
      setShouldRun(false);
      return;
    }

    const newSteps = buildSteps(location.pathname);
    if (newSteps.length === 0) {
      onComplete();
      return;
    }

    setSteps(newSteps);

    // Wait for target elements to exist in the DOM
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      const firstTarget = typeof newSteps[0].target === 'string' ? newSteps[0].target : null;
      const targetExists = firstTarget ? document.querySelector(firstTarget) !== null : true;

      if (targetExists || attempts >= 25) {
        clearInterval(interval);
        setShouldRun(true);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [run, location.pathname, buildSteps, onComplete]);

  const handleCallback = useCallback((data: any) => {
    const { type } = data;
    if (type === EVENTS.TOUR_END) {
      setShouldRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      onComplete();
    }
  }, [onComplete]);

  if (steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={shouldRun}
      continuous
      onEvent={handleCallback}
      options={{
        primaryColor: '#a855f7',
        backgroundColor: '#1C1C1C',
        textColor: '#ffffff',
        arrowColor: '#1C1C1C',
        overlayColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 10000,
      }}
      styles={{
        tooltip: {
          borderRadius: '12px',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          padding: '16px',
        },
        tooltipContainer: {
          textAlign: 'left' as const,
        },
        buttonPrimary: {
          borderRadius: '8px',
          fontWeight: 'bold',
          padding: '8px 16px',
          backgroundColor: '#a855f7',
        },
        buttonBack: {
          marginRight: '10px',
          color: '#a1a1aa',
        },
        buttonSkip: {
          color: '#a1a1aa',
        },
        buttonClose: {
          color: '#a1a1aa',
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
      }}
      locale={{
        back: t('tour.back', 'Voltar'),
        close: t('tour.close', 'Fechar'),
        last: t('tour.finish', 'Finalizar'),
        next: t('tour.next', 'Avançar'),
        skip: t('tour.skip', 'Pular Tour'),
      }}
    />
  );
};

export const isFirstVisit = (): boolean => {
  return !localStorage.getItem(TOUR_COMPLETED_KEY);
};

export default GuidedTour;
